import React, { ReactElement, useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  DefaultPageSkeleton,
  FileDropField,
  FileList,
  Icon,
  Spacer,
  Stack,
  Title,
  Typography
} from '@efast_public/fjd-component-library';
import { FormatUtil } from '../../../../shared/utils/formatUtil';

import forge from 'node-forge';
import './BundId.css';
import {
  BundIdKeyPairUploadDto,
  IBundIdGetDto
} from '../../../../api/models/ZBP/BundId';
import { convertBlobToTextString } from '../../../../shared/utils/fileUtils';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { BehApiRoutes, behClient } from '../../../../api/beh-api';
import { useNotification } from '../../../../shared/notification/useNotification';
import { useTranslation } from 'react-i18next';

type AccordionLabel = 'BundID-Postfach-Zertifikat' | 'Private Key';
type DataTestId = 'uploadFieldCertificate' | 'uploadFieldPrivateKey';
type FileType = 'zbpCertificate' | 'zbpPrivateKey';

interface KeyPairFileState {
  readonly file?: File | undefined;
  readonly validity?: Date | undefined;
  readonly uploadDate?: Date | undefined;
  readonly isValid?: boolean | undefined;
}

interface CertificateUploaderSectionProps {
  readonly fileType: FileType;
  readonly fileData: KeyPairFileState | undefined;
  readonly handleFileChange: (file: File, fileType: FileType) => void;
  readonly onFileDelete: (fileType: FileType) => void;
}

type AlertType = 'error' | 'info';

type AlertTitle =
  | 'errorTitleCertificateNotValid'
  | 'infoTitleOnCertificateIsNeeded';

interface AlertProps {
  readonly titleKind: AlertTitle;
  readonly intent: AlertType;
}

function isCertificateValid(certificateExpirationTime: Date): boolean {
  const validityDate = new Date(certificateExpirationTime);
  const today = new Date();
  return validityDate > today;
}

function InfoOrErrorBannerAlert(props: AlertProps) {
  const titleOnError =
    'Das BundID-Postfach-Zertifikat ist abgelaufen. Für die Kommunikation brauchen Sie ein aktuelles Zertifikat.';
  const infoTitle =
    'Für die Kommunikation müssen Sie ein aktuelles BundID-Postfach-Zertifikat und den Private Key hochladen. Wie Sie diese erhalten, erfahren Sie im Self-Service-Portal der BundID. Bitte beachten Sie, dass das Zertifikat nur begrenzt gültig ist.';

  const alertTitle =
    props.titleKind === 'errorTitleCertificateNotValid'
      ? titleOnError
      : infoTitle;

  return (
    <Alert
      message={alertTitle}
      variant={props.intent === 'error' ? 'error' : 'info'}
    />
  );
}

interface InfoOrErrorAlertContainerProps {
  readonly zbpKeyPair: IBundIdGetDto | undefined;
}

function InfoOrErrorBannerAlertContainer(
  props: InfoOrErrorAlertContainerProps
): ReactElement {
  return (
    <>
      {!props.zbpKeyPair ? (
        <InfoOrErrorBannerAlert
          titleKind="infoTitleOnCertificateIsNeeded"
          intent="info"
        />
      ) : (
        !isCertificateValid(props.zbpKeyPair.certificateExpirationTime) && (
          <InfoOrErrorBannerAlert
            titleKind="errorTitleCertificateNotValid"
            intent="error"
          />
        )
      )}
    </>
  );
}

function KeyPairUploadFields(props: CertificateUploaderSectionProps) {
  const label: AccordionLabel =
    props.fileType === 'zbpCertificate'
      ? 'BundID-Postfach-Zertifikat'
      : 'Private Key';
  const dataTestId: DataTestId =
    props.fileType === 'zbpCertificate'
      ? 'uploadFieldCertificate'
      : 'uploadFieldPrivateKey';

  return (
    <Stack>
      <Stack direction="row" spacing="xs" alignItems="center">
        <Icon
          data-testid={`statusIcon-${dataTestId}`}
          name={
            props.fileData
              ? props.fileData.isValid === false
                ? 'error-filled'
                : 'checkmark-filled'
              : 'circle-dash'
          }
          variant={
            props.fileData
              ? props.fileData.isValid === false
                ? 'error'
                : 'success'
              : undefined
          }
          title="Status: ok"
        />
        <Title level={6}>{label}</Title>
      </Stack>
      <Stack direction="row">
        <div style={{ paddingLeft: '1.8rem', marginBottom: '0.5rem' }}>
          <Typography variant="secondary-outline" size="s">
            {props.fileData && props.fileData.uploadDate
              ? `Hochgeladen am: ${FormatUtil.formatDateTime(props.fileData.uploadDate, 'DD.MM.YYYY')}${props.fileData.validity ? `, Gültig bis: ${FormatUtil.formatDateTime(props.fileData.validity, 'DD.MM.YYYY')}` : ', Gültig bis: unbegrenzt'}`
              : null}
          </Typography>
        </div>
      </Stack>
      {!props.fileData ? (
        <FileDropField
          className="uploadField"
          data-testid={`uploadField-${dataTestId}`}
          label="Neue Datei auswählen"
          dropHelperText={'oder in dieses Feld ziehen'}
          onAdd={(files) => props.handleFileChange(files[0], props.fileType)}
        />
      ) : (
        <FileList>
          <FileList.FileItem
            data-testid={`fileItem-${dataTestId}`}
            file={{ name: props.fileData?.file?.name ?? '' }}
            onDelete={() => props.onFileDelete(props.fileType)}
          />
        </FileList>
      )}
      <Spacer size="xs" />
    </Stack>
  );
}

export default function BundIdSettings(): ReactElement {
  const { t } = useTranslation();
  const { notifyUserFromRequest, notifyUser } = useNotification();

  const [isSaveButtonDisabled, setIsSaveButtonDisabled] =
    useState<boolean>(true);
  const [hasChanges, setHasChanges] = useState<boolean>(false);
  const [zbpCertificate, setZbpCertificate] = useState<
    KeyPairFileState | undefined
  >();
  const [zbpPrivateKey, setZbpPrivateKey] = useState<
    KeyPairFileState | undefined
  >();

  const { mutateAsync: uploadBundIdKeyPair, isPending: isUploading } =
    useMutation({
      mutationFn: (bundIdKeyPair: BundIdKeyPairUploadDto) =>
        notifyUserFromRequest(
          () => behClient.post(BehApiRoutes.bundId(), bundIdKeyPair),
          {
            successMessage: t('api.success.createBundIdKeyPair'),
            errorMessage: t('api.error.createBundIdKeyPair')
          }
        ),
      onSuccess: () => {
        mutateBundIdKeyPair();
        setHasChanges(false);
      }
    });
  const {
    data: zbpKeyPair,
    refetch: mutateBundIdKeyPair,
    isLoading: isValidating
  } = useQuery<IBundIdGetDto, AxiosError>({
    queryKey: [BehApiRoutes.bundId()]
  });

  const { mutateAsync: deleteBundIdKeyPair, isPending: isDeleting } =
    useMutation({
      mutationFn: () =>
        notifyUserFromRequest(() => behClient.delete(BehApiRoutes.bundId()), {
          successMessage: t('api.success.deleteBundIdKeyPair'),
          errorMessage: t('api.error.deleteBundIdKeyPair')
        })
    });

  useEffect(() => {
    if (zbpKeyPair) {
      setZbpCertificate({
        file: new File([''], zbpKeyPair.certificateFileName, {
          type: 'text/plain'
        }),
        uploadDate: zbpKeyPair.certificateUploadTime,
        validity: zbpKeyPair.certificateExpirationTime,
        isValid: isCertificateValid(zbpKeyPair.certificateExpirationTime)
      });
      setZbpPrivateKey({
        file: new File([''], zbpKeyPair.privateKeyFileName, {
          type: 'text/plain'
        }),
        uploadDate: zbpKeyPair.privateKeyUploadTime,
        validity: undefined
      });
    }
  }, [zbpKeyPair]);

  const onSubmit = useCallback(async () => {
    if (!zbpCertificate && !zbpPrivateKey && zbpKeyPair) {
      deleteBundIdKeyPair().catch(() => {});
      return;
    }
    const zbpCertificateText: string | undefined =
      await convertBlobToTextString(zbpCertificate?.file);
    const zbpPrivateKeyText: string | undefined = await convertBlobToTextString(
      zbpPrivateKey?.file
    );

    return uploadBundIdKeyPair({
      certificate: zbpCertificateText,
      privateKey: zbpPrivateKeyText,
      certificateFileName: zbpCertificate?.file?.name || '',
      privateKeyFileName: zbpPrivateKey?.file?.name || ''
    });
  }, [
    deleteBundIdKeyPair,
    uploadBundIdKeyPair,
    zbpCertificate,
    zbpKeyPair,
    zbpPrivateKey
  ]);

  const handleFileChange: (
    file: File,
    fileType: FileType
  ) => Promise<void> = async (
    file: File,
    fileType: FileType
  ): Promise<void> => {
    if (!file) {
      return;
    }
    setHasChanges(true);
    try {
      const filePEM: string = await file.text();
      if (fileType === 'zbpCertificate') {
        const cert = forge.pki.certificateFromPem(filePEM);

        const isRSA: boolean =
          cert?.publicKey && 'n' in cert.publicKey && 'e' in cert.publicKey;

        if (!isRSA) {
          notifyUser({
            intent: 'error',
            title:
              'Ungültiges Dateiformat. (Es wird nur eine Datei mit X.509 Formatierung akzeptiert.)'
          });
          return;
        }
        if (!isCertificateValid(cert.validity.notAfter)) {
          notifyUser({
            intent: 'error',
            title:
              'Das BundID-Postfach-Zertifikat ist abgelaufen. Für die Kommunikation brauchen Sie ein aktuelles Zertifikat.'
          });
        }

        setZbpCertificate({
          file,
          validity: cert.validity.notAfter,
          uploadDate: new Date()
        });
      } else if (fileType === 'zbpPrivateKey') {
        try {
          forge.pki.privateKeyFromPem(filePEM);
        } catch (error) {
          notifyUser({
            intent: 'error',
            title: 'Ungültiges Dateiformat. Die Datei ist kein Private Key.'
          });
          return;
        }

        setZbpPrivateKey({
          file,
          validity: undefined,
          uploadDate: new Date()
        });
      }
    } catch (error) {
      notifyUser({
        intent: 'error',
        title:
          'Fehler beim Lesen der Datei: Bitte stellen Sie sicher, dass die Datei im PEM-Format vorliegt.'
      });
    }
  };

  const handleFileDelete: (fileType: FileType) => void = (
    fileType: FileType
  ) => {
    setHasChanges(true);
    if (fileType === 'zbpCertificate') {
      setZbpCertificate(undefined);
    } else {
      setZbpPrivateKey(undefined);
    }
  };

  const handleSaveButtonDisabledStatus = useCallback(() => {
    if (!hasChanges) {
      setIsSaveButtonDisabled(true);
      return;
    }
    if (!zbpCertificate && !zbpPrivateKey && zbpKeyPair) {
      setIsSaveButtonDisabled(false);
    } else if (zbpCertificate && zbpPrivateKey) {
      setIsSaveButtonDisabled(false);
    } else {
      setIsSaveButtonDisabled(true);
    }
  }, [hasChanges, zbpCertificate, zbpKeyPair, zbpPrivateKey]);

  useEffect(() => {
    handleSaveButtonDisabledStatus();
  }, [handleSaveButtonDisabledStatus]);

  return (
    <>
      {(isDeleting || isValidating || isUploading) && <DefaultPageSkeleton />}
      <div style={{ width: '50rem' }}>
        <Stack direction="column" spacing="l">
          <InfoOrErrorBannerAlertContainer zbpKeyPair={zbpKeyPair} />
          <Title level={2} visualLevel={4}>
            Konfiguration des BundID-Postfach-Zertifikats und des Private Keys
          </Title>
          <div className="descriptionText">
            Bitte laden Sie jeweils im vorgesehenem Feld das
            BundID-Postfach-Zertifikat und den Private Key der BundID hoch.
          </div>

          <Stack alignItems="stretch" spacing="l">
            {(['zbpCertificate', 'zbpPrivateKey'] as const).map((fileType) => (
              <KeyPairUploadFields
                key={fileType}
                fileType={fileType}
                fileData={
                  fileType === 'zbpCertificate' ? zbpCertificate : zbpPrivateKey
                }
                handleFileChange={handleFileChange}
                onFileDelete={(fileType: FileType) =>
                  handleFileDelete(fileType)
                }
              />
            ))}
          </Stack>

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Stack direction="row" spacing="s">
              <Button
                disabled={isSaveButtonDisabled}
                variant="primary"
                data-testid="primary_button-save_bundID"
                onClick={onSubmit}
              >
                Speichern
              </Button>
            </Stack>
          </div>
        </Stack>
      </div>
    </>
  );
}
